import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, scopeBody, num, str } from '@/lib/overlayApi';
import type { DateRange } from '@/lib/dateRange';

export interface CompetitorSku {
  productId: string;
  sku: string;
  brand: string;
  grammage: string | null;
  mine: boolean;
  categorySharePct: number | null;
  osaPct: number | null;
  mrp: number | null;
  sp: number | null;
  discountPct: number | null;
  /** Price per unit of product (₹ per ml/g) — the like-for-like price. */
  ppu: number | null;
  sales: number;
}

/**
 * Ranked competitor SKUs inside a category — Figma 4139:74371.
 *
 * Public crawl data, so not tenant-filtered. Category share is computed on the
 * server against the whole matched set, not the visible page, so it stays a
 * share of the CATEGORY and not a share of the top 40. Prices are derived from
 * summed atoms — averaging per-row prices would weight a SKU selling one unit
 * like one selling a thousand.
 *
 * Ad SOV is deliberately absent: advertising is partner-private per brand, so
 * a competitor's ad share is not ours to show. The column states that.
 */
export function useSkuCompetition(
  customerName: string,
  category: string | undefined,
  city: string | undefined,
  range: DateRange,
  enabled: boolean,
): UseQueryResult<CompetitorSku[], Error> {
  return useQuery({
    queryKey: ['sku-competition', customerName, category, city, range],
    enabled: enabled && Boolean(category),
    staleTime: 15 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/competition/skus',
        { ...scopeBody({ customer_name: customerName, category, city }, range), limit: 40 },
        signal,
      );
      return res.rows.map((r): CompetitorSku => {
        const sp = num(r['sp']);
        const g = num(r['grammage_value']);
        return {
          productId: str(r['product_id']),
          sku: str(r['sku_name']),
          brand: str(r['brand']),
          grammage: str(r['grammage']) || null,
          mine: r['is_mine'] === true,
          categorySharePct: num(r['category_share']),
          osaPct: num(r['wt_osa']),
          mrp: num(r['mrp']),
          sp,
          discountPct: num(r['discount_pct']),
          ppu: sp != null && g != null && g > 0 ? Math.round((sp / g) * 100) / 100 : null,
          sales: num(r['sales']) ?? 0,
        };
      });
    },
  });
}
